// Какво ПРАВИ „Сторнирай всичко" / „Възстанови" с тази сметка — редовете на
// въпроса преди тях (`useBillOperationsActions.tsx`, `ConfirmConsequences`).
// Огледало на BE, което решава само:
// - `BillService.cancel_all_bill_items` (service.py) — всички редове
//   сторнирани, сумата и бакшишът 0, депозитът освободен, софтуерната
//   фактура анулирана; сметката остава в статуса си (закрита — закрита);
// - `service_restore.restore_bill` — СЪЩАТА сметка отворена наново с
//   редовете си, бакшишът махнат, депозитът остава закачен, софтуерната
//   фактура анулирана, сметката излиза от отчетите си;
// - и двете издават сторно бон, когато сметката има бон (`_storno_context`:
//   номер + закрита), а при фискална фактура — кредитно известие вместо него
//   (тогава фактурата НЕ се анулира). Сторното е винаги „В брой" (FiscalAPI
//   `storno_payments_in_cash`) — картата се връща от касата, не от терминала.
import type { TFunction } from "i18next";

import type { BillHistory } from "../../../../api/reportsBillsHistory";
import type { Consequence } from "../../../../components/ConfirmConsequences";
import type { BillFiscalInvoice } from "../../../../hooks/useBillFiscalInvoice";
import { fmtEuro } from "../../../../utils/currency";

export type ConsequenceBill = Pick<
  BillHistory,
  | "status"
  | "receipt_number"
  | "invoice_number"
  | "card_payments"
  | "deposit_amount"
  | "tip_amount"
>;

const hasReceipt = (bill: ConsequenceBill) =>
  bill.status === "closed" && Boolean((bill.receipt_number ?? "").trim());

const positive = (amount: string | null | undefined) => Number(amount ?? 0) > 0;

/** Сторно бонът / кредитното известие, или че нищо не се печата. */
const receiptLine = (
  t: TFunction,
  bill: ConsequenceBill,
  fiscalInvoice: BillFiscalInvoice | null,
): Consequence => {
  if (fiscalInvoice)
    return { text: t("billActionConfirm.creditNote", { number: fiscalInvoice.invoice_number }) };
  if (hasReceipt(bill))
    return { text: t("billActionConfirm.stornoReceipt", { receipt: bill.receipt_number }) };
  return {
    text: t(bill.status === "closed" ? "billActionConfirm.noReceipt" : "billActionConfirm.noReceiptOpen"),
  };
};

/** Платена с терминал (теглене, което не е върнато на картата) — парите се
 *  връщат в брой; връщане и през терминала е двойно връщане. */
const cardLine = (t: TFunction, bill: ConsequenceBill): Consequence | null => {
  if (bill.status !== "closed") return null;
  const charge = bill.card_payments?.find((payment) => payment.status !== "voided");
  if (!charge) return null;
  return {
    warn: true,
    text: charge.rrn
      ? t("billActionConfirm.cardRefundRrn", { rrn: charge.rrn })
      : t("billActionConfirm.cardRefund"),
  };
};

/** Софтуерната фактура се анулира; фискалната — не (кредитното известие я неутрализира). */
const invoiceLine = (
  t: TFunction,
  bill: ConsequenceBill,
  fiscalInvoice: BillFiscalInvoice | null,
  key: string,
): Consequence | null =>
  !fiscalInvoice && bill.invoice_number ? { text: t(key, { number: bill.invoice_number }) } : null;

export const cancelAllConsequences = (
  t: TFunction,
  bill: ConsequenceBill,
  fiscalInvoice: BillFiscalInvoice | null,
): Consequence[] => {
  const closed = bill.status === "closed";
  const lines: (Consequence | null)[] = [
    { text: t(closed ? "billActionConfirm.stornoClosed" : "billActionConfirm.stornoOpen") },
    receiptLine(t, bill, fiscalInvoice),
    cardLine(t, bill),
    invoiceLine(t, bill, fiscalInvoice, "billActionConfirm.stornoInvoice"),
    closed && positive(bill.deposit_amount)
      ? { text: t("billActionConfirm.depositReleased", { amount: fmtEuro(bill.deposit_amount) }) }
      : null,
    closed && positive(bill.tip_amount)
      ? { text: t("billActionConfirm.tipVoided", { amount: fmtEuro(bill.tip_amount) }) }
      : null,
    { text: t("billActionConfirm.stornoFinal") },
  ];
  return lines.filter((line): line is Consequence => line != null);
};

export const restoreConsequences = (
  t: TFunction,
  bill: ConsequenceBill,
  fiscalInvoice: BillFiscalInvoice | null,
): Consequence[] => {
  const lines: (Consequence | null)[] = [
    { text: t("billActionConfirm.restoreReopens") },
    receiptLine(t, bill, fiscalInvoice),
    cardLine(t, bill),
    invoiceLine(t, bill, fiscalInvoice, "billActionConfirm.restoreInvoice"),
    positive(bill.deposit_amount)
      ? { text: t("billActionConfirm.depositKept", { amount: fmtEuro(bill.deposit_amount) }) }
      : null,
    positive(bill.tip_amount)
      ? { text: t("billActionConfirm.tipRemoved", { amount: fmtEuro(bill.tip_amount) }) }
      : null,
    { text: t("billActionConfirm.restoreReports") },
  ];
  return lines.filter((line): line is Consequence => line != null);
};
